// src/lib/fractions.ts
/**
 * Fraction utilities for exact rational arithmetic
 * Implements Section 7 of the specification
 * 
 * All fractions maintain the invariant: d > 0, and gcd(n,d) = 1 when simplified
 * 
 * T-25: Enhanced with comprehensive locale support for decimal separator handling
 */

import { Fraction } from '../types/redux';
import { formatNumber as localeFormatNumber, LocaleCode, parseNumber } from './locale';

/**
 * Greatest Common Divisor using Euclidean algorithm
 */
function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
}

/**
 * Least Common Multiple
 */
function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b);
}

/**
 * Parse a fraction from various string formats
 * Supports: "3/4", "1.25", "150%", "42", "-2/3"
 * 
 * @param input - String to parse
 * @param opts - Options including maxDen for decimal approximation
 * @returns Fraction object
 */
export function parseFraction(input: string, opts: { maxDen?: number } = {}): Fraction {
  const { maxDen = 1000 } = opts;
  const trimmed = input.trim();
  
  if (!trimmed) {
    throw new Error('Empty input cannot be parsed as fraction');
  }
  
  // Handle percentage
  if (trimmed.endsWith('%')) {
    const percentValue = parseFloat(trimmed.slice(0, -1));
    if (isNaN(percentValue)) {
      throw new Error('Invalid percentage format');
    }
    return simplify({ n: percentValue, d: 100 });
  }
  
  // Handle fraction format "a/b"
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length !== 2) {
      throw new Error('Invalid fraction format - multiple slashes');
    }
    
    const numerator = parseInt(parts[0].trim(), 10);
    const denominator = parseInt(parts[1].trim(), 10);
    
    if (isNaN(numerator) || isNaN(denominator)) {
      throw new Error('Invalid fraction format - non-numeric parts');
    }
    
    if (denominator === 0) {
      throw new Error('Division by zero in fraction');
    }
    
    // Handle negative denominators by moving sign to numerator
    if (denominator < 0) {
      return { n: -numerator, d: -denominator };
    }
    
    return { n: numerator, d: denominator };
  }
  
  // Handle decimal format
  const decimalValue = parseFloat(trimmed);
  if (isNaN(decimalValue)) {
    throw new Error('Invalid number format');
  }
  
  // Convert decimal to fraction
  return approximate(decimalValue, maxDen);
}

/**
 * Simplify a fraction to lowest terms
 * @param f - Fraction to simplify
 * @returns Simplified fraction with gcd(n,d) = 1
 */
export function simplify(f: Fraction): Fraction {
  if (f.d === 0) {
    throw new Error('Cannot simplify fraction with zero denominator');
  }
  
  // Handle negative denominators
  let { n, d } = f;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  
  const divisor = gcd(n, d);
  return {
    n: n / divisor,
    d: d / divisor
  };
}

/**
 * Convert improper fraction to mixed number
 * @param f - Fraction to convert
 * @returns Object with whole part and remainder fraction
 */
export function toMixed(f: Fraction): { whole: number; remainder: Fraction } {
  const simplified = simplify(f);
  const whole = Math.trunc(simplified.n / simplified.d);
  const remainderNumerator = simplified.n - whole * simplified.d;
  
  return {
    whole,
    remainder: { n: remainderNumerator, d: simplified.d }
  };
}

/**
 * Convert mixed number to improper fraction
 * @param whole - Whole number part
 * @param remainder - Fractional remainder
 * @returns Improper fraction
 */
export function fromMixed(whole: number, remainder: Fraction): Fraction {
  const improperNumerator = whole * remainder.d + remainder.n;
  return { n: improperNumerator, d: remainder.d };
}

/**
 * Add two fractions
 * @param a - First fraction
 * @param b - Second fraction
 * @returns Sum as simplified fraction
 */
export function add(a: Fraction, b: Fraction): Fraction {
  const commonDenom = lcm(a.d, b.d);
  const newNumerator = a.n * (commonDenom / a.d) + b.n * (commonDenom / b.d);
  return simplify({ n: newNumerator, d: commonDenom });
}

/**
 * Subtract two fractions
 * @param a - First fraction (minuend)
 * @param b - Second fraction (subtrahend)
 * @returns Difference as simplified fraction
 */
export function sub(a: Fraction, b: Fraction): Fraction {
  const commonDenom = lcm(a.d, b.d);
  const newNumerator = a.n * (commonDenom / a.d) - b.n * (commonDenom / b.d);
  return simplify({ n: newNumerator, d: commonDenom });
}

/**
 * Multiply two fractions
 * @param a - First fraction
 * @param b - Second fraction
 * @returns Product as simplified fraction
 */
export function mul(a: Fraction, b: Fraction): Fraction {
  return simplify({
    n: a.n * b.n,
    d: a.d * b.d
  });
}

/**
 * Divide two fractions
 * @param a - Dividend fraction
 * @param b - Divisor fraction
 * @returns Quotient as simplified fraction
 */
export function div(a: Fraction, b: Fraction): Fraction {
  if (b.n === 0) {
    throw new Error('Division by zero fraction');
  }
  
  return mul(a, { n: b.d, d: b.n });
}

/**
 * Raise fraction to integer power
 * @param base - Base fraction
 * @param exp - Integer exponent (as fraction with d=1)
 * @returns Result as simplified fraction
 */
export function pow(base: Fraction, exp: Fraction): Fraction {
  if (exp.d !== 1) {
    throw new Error('Fractional exponents not supported');
  }
  
  const exponent = exp.n;
  
  if (exponent === 0) {
    return { n: 1, d: 1 };
  }
  
  if (exponent < 0) {
    // Negative exponent: invert base and use positive exponent
    if (base.n === 0) {
      throw new Error('Cannot raise zero to negative power');
    }
    const inverted = { n: base.d, d: base.n };
    return pow(inverted, { n: -exponent, d: 1 });
  }
  
  // Positive integer exponent
  let result = { n: 1, d: 1 };
  let currentBase = base;
  let currentExp = exponent;
  
  // Exponentiation by squaring for efficiency
  while (currentExp > 0) {
    if (currentExp % 2 === 1) {
      result = mul(result, currentBase);
    }
    currentBase = mul(currentBase, currentBase);
    currentExp = Math.floor(currentExp / 2);
  }
  
  return simplify(result);
}

/**
 * Convert fraction to decimal number
 * @param f - Fraction to convert
 * @returns Decimal approximation
 */
export function toNumber(f: Fraction): number {
  return f.n / f.d;
}

/**
 * Format fraction as string for display
 * @param f - Fraction to format
 * @param opts - Formatting options
 * @returns Formatted string
 */
export function formatFraction(
  f: Fraction, 
  opts: { 
    mixed?: boolean; 
    simplify?: boolean; 
    locale?: LocaleCode;
  } = {}
): string {
  const { mixed = false, simplify: shouldSimplify = true, locale = 'en-GB' } = opts;
  
  // Apply simplification if requested
  const fraction = shouldSimplify ? simplify(f) : f;
  
  // Handle special cases
  if (fraction.d === 1) {
    return localeFormatNumber(fraction.n, locale);
  }
  
  if (fraction.n === 0) {
    return '0';
  }
  
  // Format as mixed number if requested and improper
  if (mixed && Math.abs(fraction.n) >= fraction.d) {
    const mixedForm = toMixed(fraction);
    if (mixedForm.remainder.n === 0) {
      return localeFormatNumber(mixedForm.whole, locale);
    }
    
    const wholeStr = mixedForm.whole === 0 ? '' : localeFormatNumber(mixedForm.whole, locale);
    const fracStr = `${Math.abs(mixedForm.remainder.n)}/${mixedForm.remainder.d}`;
    
    if (wholeStr === '') {
      return fracStr;
    }
    
    return `${wholeStr} ${fracStr}`;
  }
  
  // Format as improper fraction
  return `${localeFormatNumber(fraction.n, locale)}/${fraction.d}`;
}

/**
 * T-25 LEGACY SUPPORT: Keep existing formatNumber function for backwards compatibility
 * But redirect to new locale-aware version
 */
function formatNumber(value: number, locale: LocaleCode): string {
  // T-25: Redirect to locale module for consistency
  return localeFormatNumber(value, locale);
}

/**
 * Approximate decimal as fraction with limited denominator
 * Uses continued fractions algorithm for best approximation
 * 
 * @param value - Decimal value to approximate
 * @param maxDen - Maximum allowed denominator
 * @returns Best fraction approximation
 */
export function approximate(value: number, maxDen = 1000): Fraction {
  if (!isFinite(value)) {
    throw new Error('Cannot approximate infinite or NaN values');
  }
  
  if (value === 0) {
    return { n: 0, d: 1 };
  }
  
  const negative = value < 0;
  const absValue = Math.abs(value);
  
  // Handle integers
  if (Number.isInteger(absValue) && absValue <= maxDen) {
    return { n: negative ? -absValue : absValue, d: 1 };
  }
  
  // Continued fractions algorithm
  let h1 = 1, h2 = 0;
  let k1 = 0, k2 = 1;
  let x = absValue;
  
  while (k1 <= maxDen) {
    const a = Math.floor(x);
    const h = a * h1 + h2;
    const k = a * k1 + k2;
    
    if (k > maxDen) break;
    
    // Check if we have exact representation
    if (Math.abs(h / k - absValue) < 1e-15) {
      return { n: negative ? -h : h, d: k };
    }
    
    // Continue with next iteration
    h2 = h1; h1 = h;
    k2 = k1; k1 = k;
    x = 1 / (x - a);
    
    // Prevent infinite loops for irrational numbers
    if (!isFinite(x)) break;
  }
  
  return { n: negative ? -h1 : h1, d: k1 };
}

/**
 * Compare two fractions
 * @param a - First fraction
 * @param b - Second fraction
 * @returns -1 if a < b, 0 if a = b, 1 if a > b
 */
export function compare(a: Fraction, b: Fraction): number {
  const diff = sub(a, b);
  if (diff.n === 0) return 0;
  return diff.n > 0 ? 1 : -1;
}

/**
 * Check if two fractions are equal
 * @param a - First fraction
 * @param b - Second fraction
 * @returns True if fractions represent the same value
 */
export function equals(a: Fraction, b: Fraction): boolean {
  return compare(a, b) === 0;
}

/**
 * Get the absolute value of a fraction
 * @param f - Fraction
 * @returns Absolute value as fraction
 */
export function abs(f: Fraction): Fraction {
  return { n: Math.abs(f.n), d: f.d };
}

/**
 * Negate a fraction
 * @param f - Fraction to negate
 * @returns Negated fraction
 */
export function negate(f: Fraction): Fraction {
  return { n: -f.n, d: f.d };
}

/**
 * Create fraction from integer
 * @param n - Integer value
 * @returns Fraction with denominator 1
 */
export function fromInteger(n: number): Fraction {
  if (!Number.isInteger(n)) {
    throw new Error('Value must be an integer');
  }
  return { n, d: 1 };
}

/**
 * Find common denominator for array of fractions
 * @param fractions - Array of fractions
 * @returns Common denominator
 */
export function findCommonDenominator(fractions: Fraction[]): number {
  if (fractions.length === 0) return 1;
  
  return fractions.reduce((acc, f) => lcm(acc, f.d), 1);
}

// Export utility functions for testing
export { gcd, lcm };

/**
 * T-25 NEW: Enhanced number formatting specifically for fraction components
 * Handles edge cases where fraction numerators/denominators might be decimal
 */
export function formatFractionComponent(value: number, locale: LocaleCode): string {
  // For fraction components, we typically expect integers
  // But handle decimal cases gracefully
  if (Number.isInteger(value)) {
    return value.toString(); // No decimal separator needed for integers
  }
  
  // If somehow we have a decimal in fraction component, use locale formatting
  return localeFormatNumber(value, locale);
}

/**
 * T-25 ENHANCED: Parsing support for locale-aware fraction input
 * Allows users to enter fractions using their locale's decimal format
 */
export function parseFractionWithLocale(
  input: string, 
  locale: LocaleCode,
  opts: { maxDen?: number } = {}
): Fraction {
  if (!input || typeof input !== 'string') {
    throw new Error('Invalid input for fraction parsing');
  }
  
  const trimmed = input.trim();
  
  try {
  
  // Handle mixed number format "a b/c" FIRST (before fraction format)
  const spaceIndex = trimmed.indexOf(' ');
  const hasSlash = trimmed.includes('/');
  const condition = spaceIndex > 0 && hasSlash;
  if (condition) {
    const wholePart = trimmed.substring(0, spaceIndex).trim();
    const fracPart = trimmed.substring(spaceIndex + 1).trim();
    
    const whole = parseNumber(wholePart, locale);
    
    // Parse the fraction part directly using the existing parseFraction logic
    const parts = fracPart.split('/');
    if (parts.length !== 2) {
      throw new Error('Invalid fraction format in mixed number');
    }
    
    const numerator = parseNumber(parts[0].trim(), locale);
    const denominator = parseNumber(parts[1].trim(), locale);
    
    if (denominator === 0) {
      throw new Error('Denominator cannot be zero');
    }
    
    const fraction = { n: numerator, d: Math.abs(denominator) };
    return fromMixed(whole, fraction);
  }
  
  // Handle fraction format "a/b" (only if not a mixed number)
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length !== 2) {
      throw new Error('Invalid fraction format');
    }
    
    // Parse numerator and denominator with locale support
    const numerator = parseNumber(parts[0].trim(), locale);
    const denominator = parseNumber(parts[1].trim(), locale);
    
    if (denominator === 0) {
      throw new Error('Denominator cannot be zero');
    }
    
    if (opts.maxDen && Math.abs(denominator) > opts.maxDen) {
      throw new Error(`Denominator exceeds maximum: ${opts.maxDen}`);
    }
    
    return { n: numerator, d: Math.abs(denominator) };
  }
  
  // Handle decimal format
  const decimal = parseNumber(trimmed, locale);
  return approximate(decimal, opts.maxDen);
  } catch (error) {
    throw error;
  }
}

/**
 * T-25 UTILITY: Format fraction for mathematical display (LaTeX)
 * This doesn't use locale since LaTeX math notation is standardized
 */
export function formatFractionForMath(fraction: Fraction, opts: { mixed?: boolean } = {}): string {
  const { mixed = false } = opts;
  
  if (fraction.d === 1) {
    return fraction.n.toString();
  }
  
  if (fraction.n === 0) {
    return '0';
  }
  
  // Mixed number format for LaTeX
  if (mixed && Math.abs(fraction.n) >= fraction.d) {
    const mixedForm = toMixed(fraction);
    if (mixedForm.remainder.n === 0) {
      return mixedForm.whole.toString();
    }
    
    const sign = fraction.n < 0 ? '-' : '';
    const whole = Math.abs(mixedForm.whole);
    const num = Math.abs(mixedForm.remainder.n);
    const den = mixedForm.remainder.d;
    
    if (whole === 0) {
      return `${sign}\\frac{${num}}{${den}}`;
    }
    
    return `${sign}${whole}\\frac{${num}}{${den}}`;
  }
  
  // Improper fraction for LaTeX
  if (fraction.n < 0) {
    return `-\\frac{${Math.abs(fraction.n)}}{${fraction.d}}`;
  }
  
  return `\\frac{${fraction.n}}{${fraction.d}}`;
}